import React, { useState, useMemo } from "react";
import { connect } from "react-redux";
import { Checkbox } from "components/common";
import { useMutation } from "@apollo/react-hooks";
import {
    CREATE_NOTE,
    REMOVE_BANK_ACCOUNT_FOR_APPROVED_USERS,
    REMOVE_BANK_ACCOUNT_FOR_REJECTED_USERS,
    REMOVE_PAYMENT_EXCLUSION_FLAG,
    UPDATE_BANK_ACCOUNT_DETAILS,
    UPDATE_DEFAULT_PAYMENT_METHOD
} from "utils/mutations";
import { Editable, Slate, withReact } from "slate-react";
import { createEditor } from "slate";
import { APPLICATION_STATUS, SUCCESS_STATUS } from "utils/constants";
import BankInfoForm from "components/common/ui/BankInfoForm";
import { capitalize } from "@tomocredit/common-fe/utils";
import SuccessMessage from "components/Popups/Messages/SuccessMessage";

const INITIAL_NOTE_VALUE = [
    {
        children: [{ text: "" }]
    }
];

const turnNoteToText = note => {
    const text = note
        .map(p => p.children.map(t => t.text).join(" "))
        .join(" ")
        .slice(0, 255)
        .trim();

    return text;
};

const ManageBankAccountModal = ({
    bankAccount,
    user,
    refetch,
    agent_email
}) => {
    const [errorMessage, setErrorMessage] = useState("");
    const [success, setSuccess] = useState(false);

    const [showEditBankDetails, setShowEditBankDetails] = useState(false);
    const [routingNumber, setRoutingNumber] = useState(null);
    const [accountNumber, setAccountNumber] = useState(null);
    const [confirmAccountNumber, setConfirmAccountNumber] = useState(null);
    const bankInfoProps = {
        routingNumber,
        setRoutingNumber,
        accountNumber,
        setAccountNumber,
        confirmAccountNumber,
        setConfirmAccountNumber
    };

    const [updateAccountDetails] = useMutation(UPDATE_BANK_ACCOUNT_DETAILS);
    const [removeBankAccountsForApprovedUsers] = useMutation(
        REMOVE_BANK_ACCOUNT_FOR_APPROVED_USERS
    );
    const [removeBankAccountsForRejectedUsers] = useMutation(
        REMOVE_BANK_ACCOUNT_FOR_REJECTED_USERS
    );
    const [updateDefaultPaymentMethod] = useMutation(
        UPDATE_DEFAULT_PAYMENT_METHOD
    );
    const [removePaymentExclusionFlag] = useMutation(
        REMOVE_PAYMENT_EXCLUSION_FLAG
    );
    const [makeDefaultPayment, setMakeDefaultPayment] = useState(false);
    const [removeAccount, setRemoveAccount] = useState(false);
    const [reauthorize, setReauthorize] = useState(false);

    const [note, setNote] = useState(INITIAL_NOTE_VALUE);
    const [createNote] = useMutation(CREATE_NOTE);
    const editor = useMemo(() => withReact(createEditor()), []);

    const handleSubmit = () => {
        // reset error
        setErrorMessage("");

        const { email } = user;
        const args = { email, agent_email };
        const paymentMethodId = parseInt(bankAccount.payment_method_id, 10);
        const mutations = [];

        if (routingNumber || accountNumber || confirmAccountNumber) {
            if (accountNumber !== confirmAccountNumber) {
                setErrorMessage("Account numbers do not match!");
                return;
            }
            mutations.push(
                Promise.resolve(
                    updateAccountDetails({
                        variables: {
                            ...args,
                            payment_method_id: paymentMethodId,
                            account_number:
                                accountNumber || bankAccount.accountNumber,
                            routing_number:
                                routingNumber || bankAccount.routingNumber,
                            type: bankAccount.type,
                            nickname: capitalize(bankAccount.type)
                        }
                    })
                ).catch(() => {
                    throw new Error("validate banking information");
                })
            );
        }

        if (makeDefaultPayment) {
            mutations.push(
                Promise.resolve(
                    updateDefaultPaymentMethod({
                        variables: {
                            ...args,
                            newDpm: paymentMethodId
                        }
                    })
                ).catch(() => {
                    throw new Error("update default payment method");
                })
            );
        }

        if (removeAccount) {
            if (user.application_status === APPLICATION_STATUS.APPROVED) {
                mutations.push(
                    Promise.resolve(
                        removeBankAccountsForApprovedUsers({
                            variables: {
                                ...args,
                                paymentMethodID: paymentMethodId
                            }
                        })
                    ).catch(() => {
                        throw new Error("remove account");
                    })
                );
            } else {
                mutations.push(
                    Promise.resolve(
                        removeBankAccountsForRejectedUsers({
                            variables: { ...args }
                        })
                    ).catch(() => {
                        throw new Error("remove account");
                    })
                );
            }
        }

        if (reauthorize) {
            mutations.push(
                Promise.resolve(
                    removePaymentExclusionFlag({
                        variables: {
                            ...args,
                            payment_method_id: paymentMethodId
                        }
                    })
                ).catch(() => {
                    throw new Error("reauthorize");
                })
            );
        }

        Promise.all(mutations)
            .then(() => {
                const text = turnNoteToText(note);
                if (text.length) {
                    const data = {
                        user_id: email,
                        body: JSON.stringify({ text: note }),
                        text,
                        tags: JSON.stringify({ tags: ["ManageBankAccount"] })
                    };
                    createNote({ variables: data })
                        .then(res => {
                            const { status } = res.data.createNote;
                            if (status === SUCCESS_STATUS) {
                                // TODO: update notes table without having to reload page
                                setSuccess(true);
                                refetch();
                            } else {
                                setErrorMessage(
                                    "Unable to save note, please try again."
                                );
                            }
                        })
                        .catch(() => {
                            setErrorMessage(
                                `Unable to save note, please try again.`
                            );
                        });
                } else {
                    setSuccess(true);
                    refetch();
                }
            })
            .catch(error => {
                setErrorMessage(`Unable to ${error}, please try again.`);
            });
    };

    return (
        <div
            style={{
                backgroundColor: "#FAFAFA",
                overflow: "auto",
                maxHeight: "80vh",
                maxWidth: "60vw"
            }}
            className="py-8 px-12"
        >
            {!success ? (
                <>
                    <h1>Manage Bank Account</h1>
                    <b>Institution:</b> {bankAccount.institution}
                    <br />
                    <b>Account Number:</b> {bankAccount.accountNumber}
                    <i
                        className="fas fa-edit"
                        onClick={() => setShowEditBankDetails(true)}
                    />
                    <br />
                    <b>Routing Number:</b> {bankAccount.routingNumber}
                    <i
                        className="fas fa-edit"
                        onClick={() => setShowEditBankDetails(true)}
                    />
                    <br />
                    {showEditBankDetails && !removeAccount && (
                        <div>
                            <BankInfoForm {...bankInfoProps} />
                            <p className="p-3 bg-white mt-4 text-base border-gray-400 border ">
                                <i>Required Scripting:</i>
                                <br />I understand you authorized TomoCredit to
                                ACH debit your account ending in XXXX for your
                                automatic payments for repayment of your
                                outstanding balance. If you have any question
                                about your payments, you must contact TomoCredit
                                3 business days before your scheduled payment at
                                866.432.7695
                            </p>
                        </div>
                    )}
                    <br />
                    Update bank account info:
                    <Checkbox
                        label={`Make default ${
                            bankAccount.is_default_payment_method
                                ? "(already default)"
                                : ""
                        }`}
                        checked={makeDefaultPayment}
                        disabled={
                            removeAccount ||
                            bankAccount.is_default_payment_method
                        }
                        onChange={() =>
                            setMakeDefaultPayment(!makeDefaultPayment)
                        }
                    />
                    <Checkbox
                        label={`Remove account ${
                            bankAccount.is_default_payment_method
                                ? "(change default payment method first)"
                                : ""
                        }`}
                        checked={removeAccount}
                        disabled={
                            bankAccount.is_default_payment_method ||
                            makeDefaultPayment
                        }
                        onChange={() => setRemoveAccount(!removeAccount)}
                    />
                    <Checkbox
                        label="Reauthorize"
                        checked={reauthorize}
                        disabled={removeAccount}
                        onChange={() => setReauthorize(!reauthorize)}
                    />
                    <div className="px-2 py-5 border-gray-400 border bg-white">
                        <Slate
                            editor={editor}
                            value={note}
                            onChange={newNote => setNote(newNote)}
                        >
                            <Editable
                                readonly={false}
                                placeholder="Enter notes here..."
                            />
                        </Slate>
                    </div>
                    <div
                        className={errorMessage ? "text-red-600 p-2" : "hidden"}
                    >
                        {errorMessage}
                    </div>
                    <button
                        type="button"
                        className="btn btn-green px-16 mt-2 flex mx-auto"
                        onClick={handleSubmit}
                    >
                        Submit
                    </button>
                </>
            ) : (
                <SuccessMessage mainMessage="Bank account updates made successfully." />
            )}
        </div>
    );
};

const mapStateToProps = state => ({
    agent_email: state.user.id
});

export default connect(mapStateToProps, null)(ManageBankAccountModal);
